import React from 'react';
import ReactDOM from 'react-dom/client';
import { Auth0Provider } from '@auth0/auth0-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import GoogleAnalytics from './components/google-analytics';
import { GlobalErrorBoundary } from './components/GlobalErrorBoundary';
import { router } from './router';
import { ThemeProvider } from './theme/ThemeProvider';
import { CustomMuiThemeProvider } from './theme/MuiThemeProvider';
import { PostHogProvider } from 'posthog-js/react';
import './index.css';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const enableAnalytics =
  import.meta.env.VITE_APP_ENV === 'production' ||
  import.meta.env.VITE_ENABLE_ANALYTICS === 'true';

const AppProviders = () => (
  <Auth0Provider
    domain={import.meta.env.VITE_AUTH0_DOMAIN}
    clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
    authorizationParams={{
      redirect_uri: import.meta.env.VITE_FRONTEND_BASE_URL + '/auth/callback',
      audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      scope: 'openid profile email offline_access',
    }}
    cacheLocation="localstorage"
    useRefreshTokens={true}
    skipRedirectCallback={false}
  >
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <CustomMuiThemeProvider>
          <RouterProvider
            router={router}
            future={{
              v7_startTransition: true,
            }}
          />
        </CustomMuiThemeProvider>
      </ThemeProvider>
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </Auth0Provider>
);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <GlobalErrorBoundary>
      <HelmetProvider>
        <GoogleAnalytics />
        {enableAnalytics ? (
          <PostHogProvider
            apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
            options={{
              api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
              capture_exceptions: true,
              debug: import.meta.env.MODE === 'development',
            }}
          >
            <AppProviders />
          </PostHogProvider>
        ) : (
          <AppProviders />
        )}
      </HelmetProvider>
    </GlobalErrorBoundary>
  </React.StrictMode>
);
